import React, { useEffect, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { carsApi, statsApi } from "../api";
import CarFormModal from "../components/CarFormModal";
import ConfirmDialog from "../components/ConfirmDialog";

export default function CarsView() {
  const [cars, setCars] = useState([]);
  const [stats, setStats] = useState({ cars: 0, bookings: 0, users: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    try {
      const [carsData, statsData] = await Promise.all([
        carsApi.list(),
        statsApi.get(),
      ]);
      setCars(carsData);
      setStats(statsData);
    } catch (e) {
      toast.error("Failed to load data", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleSave = async (data) => {
    try {
      // Map UI camelCase → API snake_case
      const payload = {
        name: data.name,
        brand: data.brand,
        model: data.model || "",
        year: +data.year || 2024,
        seats: +data.seats || 5,
        bags: +data.bags || 2,
        category: data.category,
        price: +data.price,
        transmission: data.transmission,
        fuel: data.fuel,
        description: data.description || "",
        unlimited_mileage: !!data.unlimitedMileage,
        mileage_cap: data.mileageCap || "",
        min_driver_age: +data.minDriverAge || 21,
        features: data.features || [],
        pickup_location: data.pickupLocation || data.location || "",
        dropoff_location: data.dropoffLocation || data.pickupLocation || "",
        location: data.pickupLocation || data.location || "",
        images: data.images || (data.image ? [data.image] : []),
        image: data.image || (data.images && data.images[0]) || "",
      };
      if (data.id) {
        await carsApi.update(data.id, payload);
        toast.success("Vehicle updated");
      } else {
        await carsApi.create(payload);
        toast.success("Vehicle added");
      }
      setEditing(null);
      setAdding(false);
      refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async () => {
    try {
      await carsApi.remove(confirmDel.id);
      toast.success(`Deleted "${confirmDel.name}"`);
      setConfirmDel(null);
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e.message });
    }
  };

  // Map API snake_case → UI camelCase for the form modal
  const toFormShape = (c) => ({
    ...c,
    unlimitedMileage: c.unlimited_mileage,
    mileageCap: c.mileage_cap,
    minDriverAge: c.min_driver_age,
    pickupLocation: c.pickup_location,
    dropoffLocation: c.dropoff_location,
  });

  const statCards = [
    { num: stats.cars, label: "CARS" },
    { num: stats.bookings, label: "BOOKINGS" },
    { num: stats.users, label: "USERS" },
    { num: stats.active, label: "ACTIVE" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div className="stat-card" key={s.label}>
            <div className="num">{s.num}</div>
            <div className="label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="section-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Vehicles</h2>
          <button className="pill-btn primary" onClick={() => setAdding(true)}>
            + Add Car
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading vehicles...
          </div>
        ) : cars.length === 0 ? (
          <div className="text-center text-gray-500 py-10">No vehicles yet. Click "+ Add Car" to create one.</div>
        ) : (
          <div className="space-y-3">
            {cars.map((c) => (
              <div className="list-row" key={c.id}>
                {c.image ? (
                  <img src={c.image} alt={c.name} className="car-thumb" />
                ) : (
                  <div className="car-thumb" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{c.name}</div>
                  <div className="text-sm muted">
                    {c.category} · ${c.price}/day · {c.seats} seats
                  </div>
                  <div className="text-sm flex items-center gap-1 mt-0.5 text-link-blue">
                    <MapPin size={14} className="text-red" />
                    <span>{c.location || c.pickup_location}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className="pill-btn blue" onClick={() => setEditing(toFormShape(c))}>
                    Edit
                  </button>
                  <button className="pill-btn gray" onClick={() => setConfirmDel(c)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(adding || editing) && (
        <CarFormModal
          initial={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          title="Delete vehicle"
          message={`Are you sure you want to delete "${confirmDel.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
